import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { Table, TableBody, useFirstRender, SearchActionBar } from '@insurancemenu/imenu-components-lib';
import { useLazyQuery } from '@apollo/client';
import { GET_RENEWAL_GROUPS_PAGE } from 'api/queries/groupService';
import { BiGroupsPaginationDTO } from 'types/groupService';
import { createStyles } from './styles';
import TriggerRenewalsHeader from './TriggerRenewalsTableHeader';
import TriggerRenewalsBatchSection from './TriggerRenewalsBatchSection';
import TriggerRenewalsRow from './TriggerRenewalsRow';
import EmailsModal from '../modals/EmailsModal';
import UploadExhibitModal from '../modals/UploadExhibitModal';
import WarningModal from '../modals/WarningModal';
import FiltersPanel from '../FiltersPannel';

const TriggerRenewalsContent = observer((): JSX.Element => {
  const {
    triggerRenewalsStore: {
      paginationDelegate: { filters, page, totalPages, setPage, setFilterValue, setTotalPages },
      loading,
      isEmailsModalOpen,
      isUploadModalOpen,
      getAllDomains,
      resetSelectedPeriods,
      resetAllPeriods,
    },
  } = useRootStore();

  const firstRender = useFirstRender();

  const [getRenewalGroupsPage, { data, loading: renewalsLoading }] = useLazyQuery<{
    getRenewalGroupsPage: BiGroupsPaginationDTO;
  }>(GET_RENEWAL_GROUPS_PAGE, {
    fetchPolicy: 'no-cache',
    variables: {
      effectiveDate: filters.effectiveDate,
      page,
      sponsorId: filters.sponsorId,
      name: filters.name,
      filter: filters.stage,
    },
  });

  useEffect(() => {
    if (firstRender) {
      void getRenewalGroupsPage();
      resetSelectedPeriods();
      resetAllPeriods();
    }
  }, [totalPages, page]);

  useEffect(() => {
    if (!firstRender) {
      setPage(1);
      setTotalPages(1);
      void getRenewalGroupsPage();
      resetSelectedPeriods();
      resetAllPeriods();
    }
  }, [filters.sponsorId, filters.effectiveDate, filters.name, filters.stage]);

  useEffect(() => {
    void getAllDomains();
  }, []);

  useEffect(() => {
    const groupTotalPages = data?.getRenewalGroupsPage.totalPages;
    if (groupTotalPages && totalPages !== groupTotalPages) {
      setTotalPages(data?.getRenewalGroupsPage.totalPages);
    }
  }, [data?.getRenewalGroupsPage.totalPages]);

  const onChangePageNumber = (newNumber: number): void => {
    setPage(newNumber);
    // Reset the list of periods, which vary from page to page, for the select all functionality.
    resetAllPeriods();
    resetSelectedPeriods();
  };

  const onSearch = (value: string): void => {
    setFilterValue('name', value);
    setPage(1);
    resetAllPeriods();
    resetSelectedPeriods();
  };

  const onReset = (): void => {
    setFilterValue('name', '');
    setPage(1);
    resetAllPeriods();
    resetSelectedPeriods();
  };

  const groups = data?.getRenewalGroupsPage.result || [];

  const classes = createStyles(groups.length);

  return (
    <div className={classes.content}>
      <FiltersPanel />
      <div className={classes.tableWrapper}>
        <SearchActionBar
          totalElements={data?.getRenewalGroupsPage.totalElements || 0}
          totalTitle="Total Groups Count"
          searchPlaceholder="Group Name"
          isSearchButtonDisabled={renewalsLoading || loading}
          onSearch={onSearch}
          onReset={onReset}
        />
        <TriggerRenewalsBatchSection loading={renewalsLoading || loading} />
        <Table
          className={classes.table}
          loading={renewalsLoading || loading}
          maxHeight="55rem"
          totalElements={groups.length}
          pagination={{
            onPageChange: onChangePageNumber,
            maxPage: totalPages || 1,
            tablePage: page,
            showPageInput: true,
          }}
        >
          <TriggerRenewalsHeader />
          <TableBody>
            {groups.map((group) => {
              return <TriggerRenewalsRow group={group} key={group.id} />;
            })}
          </TableBody>
        </Table>
      </div>
      <EmailsModal show={isEmailsModalOpen} />
      <UploadExhibitModal show={isUploadModalOpen} />
      <WarningModal />
    </div>
  );
});

export default TriggerRenewalsContent;
